import Modal from "@/components/ui/Modal";
import { useGetProductsQuery } from "@/redux/feature/videoDashboard/VideoDashboardApi";
import { useAppSelector } from "@/redux/hooks/hooks";
import React, { useCallback, useState } from "react";
import AddProductModal from "../VideoDashboard/components/AddProductModal";
import BrandDetailsModal from "../VideoDashboard/components/BrandDetailsModal";
import { ArrowLeft, Search } from "lucide-react";
import { Product } from "@/redux/feature/videoDashboard/videoDashboardType";
import { useGetClientCreditQuery } from "@/redux/api/nodeApi";
import toast from "react-hot-toast";
import VideoAnalysisModal from "../VideoDashboard/components/videoAnalysisModal/VideoAnalysisModal";
import ProductsTable from "../VideoDashboard/components/ProductsTable";
import { CustomButton } from "@/components/ui/Buttons";
import { useNavigate } from "react-router-dom";

const Products = () => {
  const [isBrandDetailsModalOpen, setIsBrandDetailsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState<Product[] | null>(
    null
  );
  const [isVideoAnalysisModalOpen, setIsVideoAnalysisModalOpen] =
    useState(false);
  const { selectedTeam } = useAppSelector((state) => state.global);

  const email = useAppSelector((state) => state.auth.user?.email);

  const {
    data: products,
    isLoading,
    error,
  } = useGetProductsQuery(
    { email, teamId: selectedTeam?._id },
    { pollingInterval: 30000 }
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (term.trim() === "") {
      setFilteredProducts(null);
    } else {
      const filtered =
        products?.filter(
          (product: Product) =>
            product.brandName.toLowerCase().includes(term.toLowerCase()) ||
            product.productName.toLowerCase().includes(term.toLowerCase()) ||
            product.category.toLowerCase().includes(term.toLowerCase())
        ) || [];
      setFilteredProducts(filtered);
    }
  };

  const visibleProducts = filteredProducts ?? products;
  const { data } = useGetClientCreditQuery(email as string);
  const creditBalance = data?.data?.balance;

  const [isOpen, setIsOpen] = useState(false);

  const handlerAddProduct = () => {
    if (creditBalance > 0) {
      setIsOpen(true);
    } else {
      toast.error("You do not have enough credit Balance");
    }
  };

  const navigate = useNavigate();

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <div>
      <CustomButton
        variant="outline"
        fontSize="text-xs sm:text-sm"
        fontWeight="500"
        color="#0D0D0D"
        border="1px solid #E4E4E4"
        borderRadius="30px"
        onClick={handleGoBack}
        className="flex items-center gap-x-2 sm:gap-x-2.5 px-3 py-2 sm:px-4 leading-5 sm:leading-6 mb-10"
      >
        <ArrowLeft size={18} className="sm:size-6" />
        <span className="hidden sm:inline">Go back to video dashboard</span>
        <span className="sm:hidden">Go back</span>
      </CustomButton>

      <div className="flex flex-col md:flex-row items-start justify-between mb-8 mt-5 md:mt-0 gap-5">
        <div className="space-y-2">
          <h1 className="text-gray-900 text-2xl font-semibold">All Product</h1>
          <p className="text-gray-500 text-sm">
            Manage and enhance your marketing videos
          </p>
        </div>

        <div className="flex justify-between md:justify-end gap-x-4 items-center w-full">
          {!selectedTeam?._id && (
            <button
              onClick={() => setIsVideoAnalysisModalOpen(true)}
              className="text-sm py-4 px-4 rounded-lg transition-all font-medium text-gray-600 sm:bg-transparent hover:bg-gray-50 border border-gray-200"
            >
              Video analysis
            </button>
          )}
          <button
            onClick={() => setIsBrandDetailsModalOpen(true)}
            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg border border-gray-200 transition-all duration-200"
          >
            <div className="w-8 h-8 bg-[#FF6600] rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">AI</span>
            </div>
            <div className="text-left hidden sm:block">
              <div className="font-medium text-gray-900">AI Marketplace</div>
              <div className="text-xs text-gray-500">View profile</div>
            </div>
            <svg
              className="w-4 h-4 text-gray-400 hidden sm:block"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* credit info banner */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-white text-xs font-bold">i</span>
          </div>
          <div>
            <h3 className="text-blue-900 font-medium text-sm">
              Credit Information
            </h3>
            <p className="text-blue-700 text-sm mt-1">
              After adding a product, 1 credit will be automatically deducted
              within 2 days for processing and analysis.
            </p>
          </div>
        </div>
      </div>

      {/* search and action buttons */}
      <div className="flex flex-col md:flex-row items-center justify-between mb-6 gap-y-6 w-full">
        <div className="relative w-full md:max-w-md">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={16}
          />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={handleSearch}
            className="pl-10 pr-4 py-2 w-full bg-gray-50 rounded-lg focus:outline-none focus:ring-0 focus:bg-white transition-colors"
          />
        </div>

        <div className="flex gap-x-5 items-center w-full md:w-fit justify-between md:justify-normal">
          <button
            onClick={handlerAddProduct}
            className="px-5 py-2.5 bg-[#141314] hover:bg-[#0c0c0c] text-white text-sm font-medium rounded-lg shadow-md hover:shadow-lg transition duration-200 ease-in-out"
          >
            Add New Product
          </button>
        </div>
      </div>

      {/* products table */}
      {!error && (
        <ProductsTable isLoading={isLoading} products={visibleProducts} />
      )}

      <Modal
        isOpen={isBrandDetailsModalOpen}
        setOpen={setIsBrandDetailsModalOpen}
        width="500px"
      >
        <BrandDetailsModal setIsOpen={setIsBrandDetailsModalOpen} />
      </Modal>
      <Modal
        isOpen={isVideoAnalysisModalOpen}
        setOpen={setIsVideoAnalysisModalOpen}
        width="800px"
      >
        <VideoAnalysisModal setIsOpen={setIsVideoAnalysisModalOpen} />
      </Modal>

      <Modal
        isOpen={isOpen}
        setOpen={setIsOpen}
        width="500px"
        preventCloseOnClickOutside={true}
      >
        <AddProductModal setIsOpen={setIsOpen} />
      </Modal>
    </div>
  );
};

export default Products;
